import React, { useEffect, useState } from 'react';

/**
 * Hiring manager auto-discovery, from Find Contacts on a project's
 * Companies tab: a preview of what the run will do and cost, then the run
 * itself (a background job, polled like imports), then what was found.
 *
 *   <HmDiscoveryModal projectId={…} companyIds={ids | null} onClose={…} onDone={…} />
 *
 * companyIds: the ticked company rows (null = every company in the project).
 */
type Learned = { domain: string | null; countries: string[]; fromSearch: boolean; prompts: string[]; titles: string[]; keywords: string[]; words: string[]; managerTitles?: string[] };
type Role = { role: string; n: number };
type CompanyBrief = { id: number; name: string; employees: number | null; candidates: number; roles: Role[] };
type Pick = { name: string; title: string; kind: 'hiring_manager' | 'talent_acquisition' | 'owner_founder'; level: number | null; confidence: number; why: string };
type TryResult = { company: string; employees: number | null; candidates: number; roles: Role[]; picks: Pick[]; more: number };
type Preview = {
  learned: Learned | null; canRun: boolean;
  candidates: number; companies: number; alreadyDone: number; toRun: number;
  byCompany?: CompanyBrief[]; suggestedCompanyId?: number | null;
  creditsPerCompany: number; creditsMax: number; activeCredits: number | null;
  trial: boolean; trialLimit: number; trialLeft: number | null; noCandidates: boolean;
};
type CompanyResult = { company: string; found: number; hiring_manager?: number; talent_acquisition?: number; owner_founder?: number; more?: number; error?: string };
type Job = {
  jobId: string; done: boolean; error: string | null; stage?: string; domain?: string | null;
  companiesTotal: number; companiesDone: number; inserted: number; credits: number; trial: boolean;
  companies: CompanyResult[];
  /** the table has been told this run finished (so a remount doesn't tell it again) */
  doneNotified?: boolean;
};

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });
const n = (x: number | null | undefined) => Number(x || 0).toLocaleString();

// A run in progress / just finished, per project. The table refreshes
// itself when a run completes, and that refresh remounts this modal — a
// fresh mount would show "Checking the project…" as if nothing had
// happened. Remembering the run here keeps the progress or the finished
// summary on screen; it is cleared when the finished summary is closed.
const runs = new Map<number, Job>();

export const HmDiscoveryModal: React.FC<{ projectId: number; companyIds: number[] | null; onClose: () => void; onDone: () => void }> =
  ({ projectId, companyIds, onClose, onDone }) => {
    const [preview, setPreview] = useState<Preview | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [starting, setStarting] = useState(false);
    const [job, setJob] = useState<Job | null>(() => runs.get(projectId) || null);
    useEffect(() => { if (job) runs.set(projectId, job); }, [job, projectId]);
    const finish = () => { runs.delete(projectId); onClose(); };
    // The recruiter's own words for who they place — overrides what was
    // learned from the searches and candidates (a mixed "every employee"
    // project, or a sub-team like "MEP project managers").
    const [editing, setEditing] = useState(false);
    const [description, setDescription] = useState('');
    const [applied, setApplied] = useState('');
    const [loadingPreview, setLoadingPreview] = useState(false);
    // The brief's free try: one company's picks before any credits are spent.
    const [trying, setTrying] = useState(false);
    const [tried, setTried] = useState<TryResult | null>(null);
    const [tryError, setTryError] = useState<string | null>(null);
    const tryOne = async (companyId: number | null) => {
      setTrying(true); setTryError(null);
      try {
        const r = await fetch('/api/universe/discover-hiring-managers/try', { method: 'POST', headers: auth(), body: JSON.stringify({ projectId, companyIds, companyId, description: applied }) });
        const d = await r.json();
        if (!r.ok) setTryError(d.error || 'Could not try that company'); else setTried(d);
      } catch { setTryError('Could not try that company'); }
      setTrying(false);
    };
    const KIND_LABEL: Record<Pick['kind'], string> = { hiring_manager: 'Hiring manager', talent_acquisition: 'Talent acquisition', owner_founder: 'Owner / founder' };
    const KIND_CLASS: Record<Pick['kind'], string> = { hiring_manager: 'bg-violet-100 text-violet-800', talent_acquisition: 'bg-sky-100 text-sky-800', owner_founder: 'bg-amber-100 text-amber-800' };
    const roleText = (roles: Role[]) => roles.map((r) => `${r.role} ×${r.n}`).join(' · ');

    const loadPreview = async (desc: string) => {
      setLoadingPreview(true);
      try {
        const r = await fetch('/api/universe/discover-hiring-managers/preview', { method: 'POST', headers: auth(), body: JSON.stringify({ projectId, companyIds, description: desc }) });
        const d = await r.json();
        if (!r.ok) setError(d.error || 'Could not prepare discovery');
        else { setPreview(d); setApplied(desc); setError(null); setTried(null); }
      } catch { setError('Could not prepare discovery'); }
      setLoadingPreview(false);
    };
    useEffect(() => { if (!job) loadPreview(''); // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, companyIds]);

    const start = async () => {
      setStarting(true); setError(null);
      try {
        const r = await fetch('/api/universe/discover-hiring-managers', { method: 'POST', headers: auth(), body: JSON.stringify({ projectId, companyIds, description: applied }) });
        const d = await r.json();
        if (!r.ok) { if (!d.trialBlocked) setError(d.error || 'Could not start'); setStarting(false); return; }
        setJob({ jobId: d.jobId, done: false, error: null, companiesTotal: d.total, companiesDone: 0, inserted: 0, credits: 0, trial: d.trial, companies: [] });
      } catch { setError('Could not start'); setStarting(false); }
    };

    useEffect(() => {
      if (!job?.jobId || job.done) return;
      const t = setInterval(async () => {
        try {
          const r = await fetch(`/api/universe/promote-status/${job.jobId}`, { headers: auth() });
          if (r.status === 404) { setJob((j) => (j ? { ...j, done: true, error: 'The run was interrupted (server restarted). The hiring managers found so far are in the project.' } : j)); return; }
          if (!r.ok) return;
          const d = await r.json();
          setJob((j) => (j ? { ...j, ...d } : j));
        } catch { /* keep polling */ }
      }, 1000);
      return () => clearInterval(t);
    }, [job?.jobId, job?.done]);

    // Tell the table ONCE when a run finishes. The table's refresh remounts
    // this modal with the run remembered, and telling it again on every
    // remount made it refresh in a loop.
    useEffect(() => {
      if (!job?.done || job.doneNotified) return;
      const marked = { ...job, doneNotified: true };
      runs.set(projectId, marked);
      setJob(marked);
      onDone();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [job?.done]);

    const totals = (job?.companies || []).reduce((a, c) => ({
      hm: a.hm + (c.hiring_manager || 0), ta: a.ta + (c.talent_acquisition || 0), own: a.own + (c.owner_founder || 0), more: a.more + (c.more || 0),
    }), { hm: 0, ta: 0, own: 0, more: 0 });
    const canAfford = !preview || preview.trial || preview.activeCredits == null || preview.activeCredits >= preview.creditsMax;

    return (
      <div className="modal-backdrop flex items-center justify-center" onClick={job && !job.done ? undefined : job ? finish : onClose}>
        <div className="modal-dialog p-6 max-w-xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <h3 className="text-lg font-semibold mb-1">Hiring manager auto-discovery</h3>

          {!job && (
            <>
              <p className="text-sm text-gray-600 mb-4">
                We read your mapped candidates to learn what they do, then find the people at each company who hire them:
                hiring managers by level, talent acquisition, and owners at smaller firms. Every pick comes with its reasons.
              </p>
              {error && <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
              {!preview && !error && <div className="text-sm text-gray-400">Checking the project…</div>}
              {preview && !preview.canRun && !editing && (
                <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
                  This project has no candidates or search to learn from. Map the people you place first, or{' '}
                  <button className="underline" onClick={() => setEditing(true)}>describe who you place</button>.
                </div>
              )}
              {preview && (preview.learned || editing) && (
                <div className="text-sm border border-violet-200 bg-violet-50/40 rounded-lg px-3 py-2.5 mb-3">
                  {!editing ? (
                    <>
                      <div className="text-xs font-semibold text-violet-800 uppercase tracking-wide mb-1">Looking for the managers of</div>
                      <div className="text-gray-800">{applied || preview.learned?.domain || 'the people in this project'}</div>
                      {preview.learned && (
                        <div className="text-xs text-gray-500 mt-1.5">
                          {applied ? 'Your description' : preview.learned.fromSearch ? 'From the searches this project was built from' : 'From the candidate records'}
                          {preview.learned.titles.length > 0 && <> · titles: {preview.learned.titles.slice(0, 6).join(', ')}{preview.learned.titles.length > 6 ? '…' : ''}</>}
                          {preview.learned.keywords.length > 0 && <> · keywords: {preview.learned.keywords.slice(0, 8).join(', ')}{preview.learned.keywords.length > 8 ? '…' : ''}</>}
                          {' '}· in {preview.learned.countries.join(', ')}
                        </div>
                      )}
                      <button className="text-xs text-violet-700 hover:text-violet-900 underline mt-1.5" onClick={() => { setDescription(applied); setEditing(true); }}>
                        {applied ? 'Change' : 'Not quite right? Describe who you place'}
                      </button>
                      {!applied && (
                        <div className="text-[11px] text-gray-400 mt-1">
                          Each company is judged by the candidates mapped there. If the project mixes professions (one title, several trades), say which one you mean.
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="text-xs font-semibold text-violet-800 uppercase tracking-wide mb-1">Who do you place?</div>
                      <textarea
                        className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-violet-400"
                        rows={2} maxLength={500} autoFocus
                        placeholder="e.g. MEP project managers at general contractors · embedded C++ developers for automotive"
                        value={description} onChange={(e) => setDescription(e.target.value)}
                      />
                      <div className="flex gap-2 mt-1.5">
                        <button className="px-3 py-1 text-xs font-semibold text-white bg-violet-600 rounded hover:bg-violet-700 disabled:opacity-50"
                          disabled={loadingPreview || !description.trim()} onClick={async () => { await loadPreview(description.trim()); setEditing(false); }}>
                          {loadingPreview ? 'Reading…' : 'Use this'}
                        </button>
                        <button className="px-3 py-1 text-xs text-gray-500 hover:text-gray-800" onClick={() => setEditing(false)}>Cancel</button>
                      </div>
                    </>
                  )}
                </div>
              )}
              {preview && preview.canRun && !editing && (
                <div className="text-sm mb-3 space-y-2">
                  {preview.learned && preview.learned.managerTitles && preview.learned.managerTitles.length > 0 && (
                    <div className="text-xs text-gray-600">
                      <span className="font-semibold text-gray-700">It will look for titles like:</span>{' '}
                      {preview.learned.managerTitles.slice(0, 8).join(', ')}{preview.learned.managerTitles.length > 8 ? '…' : ''}
                      {' '}— plus talent acquisition, and owners at firms of 250 or fewer. Each company is judged by the candidates mapped there.
                    </div>
                  )}
                  {preview.byCompany && preview.byCompany.length > 0 && (
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 bg-gray-50 border-b border-gray-200">
                        {n(preview.toRun)} compan{preview.toRun === 1 ? 'y' : 'ies'} and what your candidates are called there
                      </div>
                      <div className="max-h-36 overflow-auto divide-y divide-gray-100 text-xs">
                        {preview.byCompany.map((c) => (
                          <div key={c.id} className="flex justify-between gap-3 px-3 py-1.5">
                            <span className="text-gray-800 truncate">{c.name}</span>
                            <span className="text-gray-500 truncate text-right">{c.roles.length ? roleText(c.roles) : 'no candidates mapped here — uses the project’s'}</span>
                          </div>
                        ))}
                        {preview.toRun > preview.byCompany.length && <div className="px-3 py-1.5 text-gray-400">+{n(preview.toRun - preview.byCompany.length)} more</div>}
                      </div>
                    </div>
                  )}
                  {!tried && preview.suggestedCompanyId != null && (
                    <div className="flex items-center gap-3">
                      <button
                        className="px-3 py-1.5 text-xs font-semibold text-violet-700 border border-violet-300 rounded-lg hover:bg-violet-50 disabled:opacity-50"
                        disabled={trying || loadingPreview}
                        onClick={() => tryOne(preview.suggestedCompanyId || null)}
                      >
                        {trying ? 'Judging one company…' : `Try ${preview.byCompany?.[0]?.name || 'one company'} first — free`}
                      </button>
                      <span className="text-[11px] text-gray-400">See real picks with reasons before any credits are spent. Takes about a minute.</span>
                    </div>
                  )}
                  {tryError && <div className="text-xs text-red-600">{tryError}</div>}
                  {tried && (
                    <div className="border border-violet-200 rounded-lg overflow-hidden">
                      <div className="px-3 py-1.5 text-xs bg-violet-50/60 border-b border-violet-200 flex justify-between gap-3">
                        <span><span className="font-semibold text-violet-900">{tried.company}</span>{tried.employees ? <span className="text-gray-500"> · {n(tried.employees)} people</span> : null}</span>
                        <span className="text-gray-500 truncate">{tried.roles.length ? `your candidates there: ${roleText(tried.roles)}` : 'no candidates mapped there'}</span>
                      </div>
                      {tried.picks.length === 0 ? (
                        <div className="px-3 py-3 text-xs text-gray-500">Nobody confident enough at this company. Try describing who you place, or run anyway — companies with no match are never charged.</div>
                      ) : (
                        <div className="max-h-48 overflow-auto divide-y divide-gray-100">
                          {tried.picks.map((p, i) => (
                            <div key={i} className="px-3 py-1.5 text-xs">
                              <div className="flex items-center gap-2">
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${KIND_CLASS[p.kind]}`}>{KIND_LABEL[p.kind]}{p.level ? ` L${p.level}` : ''}</span>
                                <span className="font-medium text-gray-800 truncate">{p.title}</span>
                                <span className="ml-auto text-gray-500 tabular-nums">{p.confidence}%</span>
                              </div>
                              {p.why && <div className="text-gray-400 truncate mt-0.5">{p.why}</div>}
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="px-3 py-2 border-t border-violet-200 flex items-center justify-between gap-3 bg-white">
                        <span className="text-[11px] text-gray-500">{tried.more > 0 ? `${n(tried.more)} more likely hiring managers over the 10 cap.` : 'Looks right? Start the run below.'}</span>
                        <button className="text-xs text-violet-700 hover:text-violet-900 underline" onClick={() => { setDescription(applied); setEditing(true); setTried(null); }}>
                          Not right — describe who you place
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {preview && preview.canRun && (
                <div className="text-sm text-gray-700 space-y-1.5 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
                  <div><span className="font-semibold">{n(preview.toRun)}</span> compan{preview.toRun === 1 ? 'y' : 'ies'} to search
                    {preview.alreadyDone > 0 && <span className="text-gray-500"> · {n(preview.alreadyDone)} already done (skipped, not charged)</span>}
                  </div>
                  <div>Learning from <span className="font-semibold">{n(preview.candidates)}</span> mapped candidates</div>
                  {preview.trial ? (
                    <div className="text-teal-800">
                      Free trial: up to {preview.trialLimit} companies at no cost
                      {preview.trialLeft != null && preview.trialLeft < preview.toRun && <> — <span className="font-semibold">{preview.trialLeft} left</span>, so this run covers the {preview.trialLeft} best-mapped</>}.
                    </div>
                  ) : (
                    <div>
                      Up to <span className="font-semibold">{n(preview.creditsMax)} credits</span> ({preview.creditsPerCompany} per company where someone is found; nothing for companies with no match)
                      {preview.activeCredits != null && <span className="text-gray-500"> · you have {n(preview.activeCredits)}</span>}
                    </div>
                  )}
                  {!canAfford && <div className="text-red-600">Not enough credits for every company — the run stops when they run out. Top up in Settings → Credits.</div>}
                </div>
              )}
              <div className="flex justify-end gap-2 mt-4">
                <button className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-800" onClick={onClose}>Cancel</button>
                <button
                  className="px-4 py-1.5 text-sm font-semibold text-white bg-violet-600 rounded-lg hover:bg-violet-700 disabled:opacity-50"
                  disabled={!preview || !preview.canRun || !preview.toRun || starting || editing || loadingPreview || trying || (preview.trial && preview.trialLeft === 0)}
                  onClick={start}
                >
                  {starting ? 'Starting…' : 'Start discovery'}
                </button>
              </div>
            </>
          )}

          {job && !job.done && (
            <>
              <div className="text-sm text-gray-600 mb-3">{job.stage || 'Starting…'}</div>
              <div className="w-full bg-gray-100 rounded-full h-2 mb-2">
                <div className="bg-violet-600 h-2 rounded-full transition-all" style={{ width: `${job.companiesTotal ? Math.round((job.companiesDone / job.companiesTotal) * 100) : 0}%` }} />
              </div>
              <div className="text-xs text-gray-500 tabular-nums">
                {n(job.companiesDone)} of {n(job.companiesTotal)} companies · {n(job.inserted)} added so far{!job.trial && job.credits ? ` · ${n(job.credits)} credits` : ''}
              </div>
              {job.domain && <div className="mt-3 text-xs text-gray-500"><span className="font-semibold text-gray-600">Your candidates:</span> {job.domain}</div>}
              <div className="mt-3 text-xs text-gray-400">This runs in the background — you can close this and carry on; the hiring managers appear on the Hiring managers tab as each company finishes.</div>
              <div className="flex justify-end mt-3">
                <button className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-800" onClick={onClose}>Close</button>
              </div>
            </>
          )}

          {job && job.done && (
            <>
              {job.error && <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{job.error}</div>}
              <div className="text-sm text-gray-700 mb-2">
                <span className="font-semibold">{n(job.inserted)}</span> added across {n(job.companiesDone)} compan{job.companiesDone === 1 ? 'y' : 'ies'}:
                {' '}{n(totals.hm)} hiring managers · {n(totals.ta)} talent acquisition · {n(totals.own)} owners / founders
                {!job.trial && job.credits ? ` · ${n(job.credits)} credits` : ''}
              </div>
              {totals.more > 0 && <div className="text-xs text-gray-500 mb-2">{n(totals.more)} more likely hiring managers were over the 10-per-company cap.</div>}
              <div className="max-h-64 overflow-auto border border-gray-200 rounded-lg text-xs">
                {job.companies.map((c, i) => (
                  <div key={i} className="flex justify-between gap-3 px-3 py-1.5 border-b border-gray-100 last:border-0">
                    <span className="text-gray-800 truncate">{c.company}</span>
                    <span className="text-gray-500 whitespace-nowrap tabular-nums">
                      {c.error ? <span className="text-red-600">failed</span> : c.found ? `${c.hiring_manager || 0} HM · ${c.talent_acquisition || 0} TA · ${c.owner_founder || 0} owner` : 'nobody confident'}
                    </span>
                  </div>
                ))}
              </div>
              <div className="text-xs text-gray-500 mt-2">Direct hiring managers are starred. The "Why" column on the Hiring managers tab shows each pick's reasons.</div>
              <div className="flex justify-end mt-4">
                <button className="px-4 py-1.5 text-sm font-semibold text-white bg-violet-600 rounded-lg hover:bg-violet-700" onClick={finish}>Done</button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

export default HmDiscoveryModal;
